/**
 * /staff — who can sign in, and inviting someone new.
 *
 * The last "coming soon" in the sidebar. Two things: the people the caller
 * can see (RLS on users / user_roles decides), and an invite form that hands
 * the backend exactly what POST /invites already accepted from the command
 * line. Privilege rules live in the backend (a hub role may only be granted
 * by a hub role); this page just says what it was told.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { useAuth } from '../auth/useAuth.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import Field from '../components/Field.jsx';
import Select from '../components/Select.jsx';

const ROLES = [
  { value: 'instructor', label: 'Instructor — runs sessions, verifies, awards' },
  { value: 'sto', label: 'Shift Training Officer — runs tailgate sessions' },
  { value: 'coordinator', label: 'Training Coordinator — builds sections, binds, manages sessions' },
  { value: 'supervisor', label: 'Supervisor — sees compliance for their people' },
  { value: 'agency_admin', label: 'Agency Administrator' },
  { value: 'medical_director', label: 'Medical Director (agency)' },
  { value: 'analyst', label: 'Analyst (read-only)' },
  { value: 'hub_coordinator', label: 'Training Coordinator (Hub)' },
  { value: 'program_chair', label: 'Program Chair (Hub)' },
  { value: 'hub_analyst', label: 'Analyst (Hub)' },
  { value: 'medical_director_hub', label: 'Medical Director (Hub)' },
  { value: 'system_admin', label: 'System Administrator' },
];

export function StaffPage() {
  const { profile } = useAuth();
  const [people, setPeople] = useState(undefined);
  const [institutions, setInstitutions] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({ fullName: '', email: '', roleKey: 'instructor', institutionId: '' });
  const canInvite = profile?.permissions?.includes('role.grant') ?? false;

  const load = useCallback(async () => {
    try {
      const [staff, insts] = await Promise.all([apiFetch('/api/v1/staff'), apiFetch('/api/v1/institutions').catch(() => [])]);
      setPeople(staff ?? []);
      setInstitutions(insts ?? []);
      setForm((f) => ({ ...f, institutionId: f.institutionId || profile?.institution?.id || '' }));
    } catch (err) {
      setError(err);
      setPeople(null);
    }
  }, [profile?.institution?.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function invite(event) {
    event.preventDefault();
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const r = await apiFetch('/api/v1/invites', {
        method: 'POST',
        body: JSON.stringify({
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          roleKey: form.roleKey,
          institutionId: form.institutionId,
        }),
      });
      setResult(r);
      setForm((f) => ({ ...f, fullName: '', email: '' }));
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Staff</h1>
      </div>
      <ErrorBanner error={error} />
      <p className="muted">
        The people who can sign in to Attest and what they can do. Participants are not here — they
        never sign in; their phone knows them.
      </p>

      <section className="panel" aria-labelledby="people-heading">
        <h2 id="people-heading">People you can see</h2>
        {people === undefined && (
          <p className="muted" aria-busy="true">
            Loading…
          </p>
        )}
        {people && people.length === 0 && <p className="muted">Nobody yet.</p>}
        {people && people.length > 0 && (
          <div className="schedule__scroll">
            <table className="schedule__table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Institution</th>
                  <th scope="col">Roles</th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <tr key={p.id}>
                    <td>{p.full_name}</td>
                    <td className="muted">{p.email}</td>
                    <td>{p.institution?.name ?? <span className="muted">—</span>}</td>
                    <td>{p.roles.map((r) => r.name).join(', ') || <span className="muted">none</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {canInvite && (
        <form className="panel" onSubmit={invite} aria-labelledby="invite-heading">
          <h2 id="invite-heading">Invite someone</h2>
          <p className="muted">
            They get an email with a link to set their password. If the mail cannot be sent, the link
            appears here for you to hand over.
          </p>
          {result && (
            <p className="callout callout--ok">
              <strong>
                {result.repaired ? 'Account repaired' : 'Invited'} — {result.email}.
              </strong>
              <span className="callout__detail">
                {result.mailed ? (
                  'The link has been emailed.'
                ) : (
                  <>
                    Mail did not go out. Hand them this link (it works once):{' '}
                    <code className="staff__link">{result.actionLink}</code>
                  </>
                )}
              </span>
            </p>
          )}
          <div className="field-row">
            <Field label="Full name" value={form.fullName} onChange={(v) => setForm((f) => ({ ...f, fullName: v }))} disabled={busy} />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} disabled={busy} />
          </div>
          <div className="field-row">
            <Select
              label="Role"
              value={form.roleKey}
              onChange={(v) => setForm((f) => ({ ...f, roleKey: v }))}
              options={ROLES}
              disabled={busy}
            />
            <Select
              label="Institution"
              value={form.institutionId}
              onChange={(v) => setForm((f) => ({ ...f, institutionId: v }))}
              options={institutions.map((i) => ({ value: i.id, label: i.name }))}
              placeholder="Choose…"
              disabled={busy}
            />
          </div>
          <p className="toolbar">
            <button
              type="submit"
              className="btn btn--primary btn--auto"
              disabled={busy || !form.fullName.trim() || !form.email.trim() || !form.institutionId}
            >
              {busy ? 'Sending…' : 'Send the invitation'}
            </button>
          </p>
        </form>
      )}
    </>
  );
}

export default StaffPage;
